// Las piezas de adentro del cajón: la tarjeta, su rejilla de campos, el chip de estado y el
// vacío. Salen del CSS del mock (`_shared/ficha-cliente-360.css`), medida por medida.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Una tarjeta del cajón.
 *
 * `overflow-hidden` no es cosmético: sin él, una tabla o una lista al ras se le sale por las
 * esquinas redondeadas.
 */
export function FileCard({
    title,
    meta,
    flush = false,
    children,
    className,
}: {
    title: ReactNode;
    /** Lo que va a la derecha del título — un conteo, un chip. */
    meta?: ReactNode;
    /** Cuerpo al ras, sin padding: para tablas y listas de filas completas. */
    flush?: boolean;
    children: ReactNode;
    className?: string;
}) {
    return (
        <section
            className={cn('overflow-hidden rounded-md border border-border bg-card', className)}
        >
            <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-3 py-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.04em]">{title}</h3>
                {meta === undefined ? null : (
                    <span className="text-[10.5px] tabular-nums text-muted-foreground">{meta}</span>
                )}
            </div>
            <div className={flush ? '' : 'px-3 py-2.5'}>{children}</div>
        </section>
    );
}

/** La rejilla de dos columnas del mock. Los campos van dentro como `KvItem`. */
export function KvGrid({ children, className }: { children: ReactNode; className?: string }) {
    return <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-2', className)}>{children}</dl>;
}

/**
 * Un campo de la rejilla: rótulo arriba, valor abajo.
 *
 * El valor NO se trunca. Una CURP o un nombre largo cortados en silencio le hacen creer al analista
 * que ese es el dato completo; envolver es feo un renglón, mentir es peor.
 */
export function KvItem({
    label,
    title,
    mono = false,
    wide = false,
    children,
}: {
    label: string;
    /** Por qué el rótulo dice lo que dice. Sale como tooltip. */
    title?: string;
    /** Monoespaciada: para CURP, RFC, CLABE, IMEI y folios. */
    mono?: boolean;
    /** A lo ancho de las dos columnas: para direcciones y textos largos. */
    wide?: boolean;
    children: ReactNode;
}) {
    return (
        <div className={cn('min-w-0', wide && 'col-span-2')}>
            <dt
                title={title}
                className={cn(
                    'text-[9.5px] font-bold uppercase tracking-[0.06em] text-muted-foreground',
                    title !== undefined && 'cursor-help',
                )}
            >
                {label}
            </dt>
            <dd
                className={cn(
                    'mt-0.5 break-words',
                    mono ? 'font-mono text-[11px]' : 'text-[12px]',
                )}
            >
                {children}
            </dd>
        </div>
    );
}

export type ChipTone = 'success' | 'danger' | 'warning' | 'info' | 'muted';

const CHIP_TONE_CLASS: Record<ChipTone, string> = {
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-danger',
    warning: 'bg-warning-soft text-warning',
    info: 'bg-info-soft text-info',
    muted: 'bg-muted text-muted-foreground',
};

/** El chip de estado del mock: fondo suave, sin borde, redondo. */
export function Chip({
    tone = 'muted',
    children,
    className,
}: {
    tone?: ChipTone;
    children: ReactNode;
    className?: string;
}) {
    return (
        <span
            className={cn(
                'inline-block rounded-full px-1.5 py-px text-[9.5px] font-bold',
                CHIP_TONE_CLASS[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

/**
 * Una pestaña que quedó sin nada que mostrar, con el motivo a la vista.
 *
 * Es para el vacío LEÍDO: la lectura salió bien y no había filas. El que falló o el que nadie
 * permitió tienen su propio bloque y no se dicen así.
 */
export function DrawerEmpty({ children }: { children: ReactNode }) {
    return (
        <div className="px-5 py-8 text-center text-[12px] italic text-muted-foreground">
            {children}
        </div>
    );
}
